import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { requestEmailCode, verifyEmailCode } from '../../lib/api';
import { clearCodeFlow, saveCodeFlow, codeError } from '../../lib/emailCodeFlow';

export default function EmailCodeChallenge({ flow, onChange, onComplete, onBack }) {
  const { i18n } = useTranslation();
  const sv = (i18n.resolvedLanguage || i18n.language || 'sv').startsWith('sv');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [time, setTime] = useState(Date.now());
  const lock = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    const id = setInterval(() => setTime(Date.now()), 1000);
    return () => { mounted.current = false; clearInterval(id); };
  }, []);
  const wait = Math.max(0, Math.ceil((flow.resendAt - time) / 1000));
  const expired = time >= flow.expiresAt;
  async function perform(resend = false) {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError('');
    try {
      if (resend) {
        const result = await requestEmailCode(flow.identifier, flow.purpose);
        if (!mounted.current) return;
        onChange(saveCodeFlow(result.data, flow.identifier, flow.purpose));
        setCode(''); setTime(Date.now());
      } else {
        const result = await verifyEmailCode(flow.challenge_id, code, flow.purpose);
        if (!mounted.current) return;
        clearCodeFlow(flow.purpose);
        await onComplete(result.data);
      }
    } catch (err) {
      if (mounted.current) setError(codeError(err, sv));
    } finally {
      lock.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return <div className="space-y-5 text-left">
    <div className="space-y-2">
      <h1 className="text-2xl font-semibold">{sv ? 'Ange din mejlkod' : 'Enter your email code'}</h1>
      <p className="text-sm text-muted-foreground">{sv
        ? 'Om kontot kan ta emot mejl skickas en sexsiffrig kod till kontots e-post eller kontaktadress. Kontrollera även skräpposten.'
        : 'If the account can receive email, a six-digit code is sent to its email or contact address. Please check spam too.'}</p>
      <p className="text-sm font-medium break-all">{flow.identifier}</p>
      <p className="text-sm text-muted-foreground">{sv ? 'Läs mejlet och gå sedan tillbaka hit. Koden gäller i 10 minuter.' : 'Read the email, then return here. The code is valid for 10 minutes.'}</p>
    </div>
    <form className="space-y-4" onSubmit={e => { e.preventDefault(); if (/^[0-9]{6}$/.test(code) && !expired) perform(); }}>
      <Label htmlFor="email-code">{sv ? 'Sexsiffrig kod' : 'Six-digit code'}</Label>
      <Input id="email-code" type="text" inputMode="numeric" autoComplete="one-time-code"
        maxLength={6} pattern="[0-9]{6}" required autoFocus value={code} disabled={busy || expired}
        onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        onPaste={e => { e.preventDefault(); setCode(e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)); }}
        className="h-14 text-center text-2xl font-semibold tracking-[0.35em]" aria-describedby="code-status" />
      <p id="code-status" role="status" className="text-sm text-muted-foreground">{expired ? (sv ? 'Koden har gått ut. Begär en ny nedan.' : 'Code expired. Request a new one below.') : ''}</p>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <Button className="w-full h-12" type="submit" disabled={busy || expired || code.length !== 6}>
        {busy ? (sv ? 'Vänta…' : 'Please wait…') : flow.purpose === 'reset' ? (sv ? 'Fortsätt till nytt lösenord' : 'Continue to new password') : (sv ? 'Logga in' : 'Sign in')}
      </Button>
    </form>
    <div className="space-y-2">
      <Button variant="outline" className="w-full" disabled={busy || wait > 0} onClick={() => perform(true)}>
        {wait > 0 ? (sv ? `Ny kod om ${wait} s` : `New code in ${wait}s`) : (sv ? 'Skicka en ny kod' : 'Send a new code')}
      </Button>
      <Button variant="ghost" className="w-full" disabled={busy} onClick={() => { clearCodeFlow(flow.purpose); onBack(); }}>
        {sv ? 'Tillbaka' : 'Back'}
      </Button>
    </div>
  </div>;
}
